import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

class DriverMessagingService {
  constructor() {
    this.collectionName = 'driver_messages';
  }

  /**
   * Sendet eine Nachricht an einen spezifischen Fahrer
   * @param {string} recipientId - Die UID des Fahrers
   * @param {string} title - Nachrichtentitel
   * @param {string} message - Nachrichtentext
   * @param {string} priority - Priorität: 'normal' oder 'high'
   * @param {Date|null} expiresAt - Optionales Ablaufdatum
   * @returns {Promise<string>} Dokument-ID der erstellten Nachricht
   */
  async sendMessageToDriver(recipientId, title, message, priority = 'normal', expiresAt = null) {
    try {
      const messageData = {
        recipientId,
        title,
        message,
        priority,
        type: 'central_message',
        createdAt: serverTimestamp(),
        readBy: [],
        status: 'active'
      };

      if (expiresAt) {
        messageData.expiresAt = Timestamp.fromDate(expiresAt);
      }

      const docRef = await addDoc(collection(db, this.collectionName), messageData);
      console.log('Nachricht an Fahrer gesendet:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht an Fahrer:', error);
      throw error;
    }
  }

  /**
   * Sendet eine Broadcast-Nachricht an alle Fahrer
   * @param {string} title - Nachrichtentitel
   * @param {string} message - Nachrichtentext
   * @param {string} priority - Priorität: 'normal' oder 'high'
   * @param {Date|null} expiresAt - Optionales Ablaufdatum
   * @returns {Promise<string>} Dokument-ID der erstellten Nachricht
   */
  async sendBroadcastMessage(title, message, priority = 'normal', expiresAt = null) {
    try {
      const messageData = {
        recipientId: 'all',
        title,
        message,
        priority,
        type: 'central_message',
        createdAt: serverTimestamp(),
        readBy: [],
        status: 'active'
      };

      if (expiresAt) {
        messageData.expiresAt = Timestamp.fromDate(expiresAt);
      }

      const docRef = await addDoc(collection(db, this.collectionName), messageData);
      console.log('Broadcast-Nachricht gesendet:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Fehler beim Senden der Broadcast-Nachricht:', error);
      throw error;
    }
  }

  /**
   * Sendet mehrere Test-Nachrichten hintereinander
   * @param {string} recipientId - Die UID des Fahrers oder 'all'
   * @param {Array<{title: string, message: string}>} messages - Array von Nachrichten
   * @param {number} delayMs - Verzögerung zwischen Nachrichten in Millisekunden
   * @returns {Promise<Array<string>>} Array der Dokument-IDs
   */
  async sendMultipleMessages(recipientId, messages, delayMs = 1000) {
    try {
      const messageIds = [];
      
      for (const msg of messages) {
        const messageId = await this.sendMessageToDriver(
          recipientId, 
          msg.title, 
          msg.message, 
          'normal'
        );
        messageIds.push(messageId);
        
        // Warten zwischen den Nachrichten
        if (delayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      
      return messageIds;
    } catch (error) {
      console.error('Fehler beim Senden mehrerer Nachrichten:', error);
      throw error;
    }
  }

  /**
   * Lädt alle gesendeten Nachrichten (für Admin-Übersicht)
   * @param {number} limitCount - Maximale Anzahl der Nachrichten
   * @returns {Promise<Array>} Array der Nachrichten
   */
  async fetchSentMessages(limitCount = 50) {
    try {
      const messagesQuery = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(messagesQuery);
      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return messages.slice(0, limitCount);
    } catch (error) {
      console.error('Fehler beim Laden der gesendeten Nachrichten:', error);
      throw error;
    }
  }

  /**
   * Lädt Nachrichten für einen spezifischen Fahrer
   * @param {string} driverId - Die UID des Fahrers
   * @returns {Promise<Array>} Array der Nachrichten für den Fahrer
   */
  async fetchMessagesForDriver(driverId) {
    try {
      const messagesQuery = query(
        collection(db, this.collectionName),
        where('recipientId', 'in', [driverId, 'all']),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(messagesQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Fehler beim Laden der Fahrer-Nachrichten:', error);
      throw error;
    }
  }

  /**
   * Erstellt eine Test-Nachricht für sofortige Anzeige
   * @param {string} recipientId - Die UID des Fahrers oder 'all'
   * @returns {Promise<string>} Dokument-ID der Test-Nachricht
   */
  async createTestMessage(recipientId = 'all') {
    const testMessages = [
      {
        title: 'Test-Nachricht',
        message: 'Dies ist eine Test-Nachricht vom Admin-Panel. Sie wird automatisch nach 3 Sekunden geschlossen.'
      },
      {
        title: 'Verkehrsstörung',
        message: 'Aufgrund von Bauarbeiten ist die Hauptstraße ab 14:00 Uhr gesperrt. Bitte nutzen Sie alternative Routen.'
      },
      {
        title: 'Systemwartung',
        message: 'Das Buchungssystem wird in 30 Minuten für 15 Minuten gewartet. Bitte informieren Sie Ihre Kunden entsprechend.'
      }
    ];

    const randomMessage = testMessages[Math.floor(Math.random() * testMessages.length)];
    
    return await this.sendMessageToDriver(
      recipientId,
      randomMessage.title,
      randomMessage.message,
      'normal'
    );
  }

  /**
   * Erstellt mehrere Test-Nachrichten für Warteschlangen-Test
   * @param {string} recipientId - Die UID des Fahrers oder 'all'
   * @returns {Promise<Array<string>>} Array der Dokument-IDs
   */
  async createMultipleTestMessages(recipientId = 'all') {
    const messages = [
      {
        title: 'Nachricht 1',
        message: 'Erste Test-Nachricht. Diese wird 3 Sekunden angezeigt.'
      },
      {
        title: 'Nachricht 2',
        message: 'Zweite Test-Nachricht. Diese wird nach der ersten angezeigt.'
      },
      {
        title: 'Nachricht 3',
        message: 'Dritte Test-Nachricht. Alle Nachrichten landen im Glocken-Button.'
      }
    ];

    return await this.sendMultipleMessages(recipientId, messages, 1000);
  }
}

// Singleton-Instanz exportieren
const driverMessagingService = new DriverMessagingService();
export default driverMessagingService;